class FitStylePicker extends HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.section;
		this.location = this.dataset.location || '';

		this.dialog = this.querySelector('[data-fs-dialog]');
		this.triggerBtn = this.querySelector('[data-fs-open]');
		this.closeBtn = this.querySelector('[data-fs-close]');
		this.doneBtn = this.querySelector('[data-fs-done]');
		this.cards = Array.from(this.querySelectorAll('[data-fs-card]'));
		this.footerSwatch = this.querySelector('[data-fs-footer-swatch]');
		this.footerName = this.querySelector('[data-fs-footer-name]');
		this.footerPrice = this.querySelector('[data-fs-footer-price]');
		this.dialogImages = Array.from(this.querySelectorAll('[data-fs-card] img'));

		this.pendingCard = this.cards.find((c) => c.classList.contains('is-selected')) || null;

		this.triggerBtn?.addEventListener('click', () => this.open());
		this.triggerBtn?.addEventListener('pointerenter', () => this.prepareImages(), { once: true });
		this.triggerBtn?.addEventListener('touchstart', () => this.prepareImages(), { once: true, passive: true });
		this.triggerBtn?.addEventListener('focus', () => this.prepareImages(), { once: true });
		this.closeBtn?.addEventListener('click', () => this.close());
		this.dialog?.addEventListener('cancel', (e) => {
			e.preventDefault();
			this.close();
		});
		this.dialog?.addEventListener('click', (e) => {
			if (e.target === this.dialog) this.close();
		});

		this.cards.forEach((card) => {
			card.addEventListener('click', () => this.pickCard(card));
		});

		this.doneBtn?.addEventListener('click', () => this.commit());
	}

	open({ instant = false } = {}) {
		if (!this.dialog) return;
		this.prepareImages();
		if (instant) this.dialog.classList.add('no-transition');
		if (this.dialog.showModal) {
			try {
				this.dialog.showModal();
			} catch (_) {
				this.dialog.setAttribute('open', '');
			}
		} else {
			this.dialog.setAttribute('open', '');
		}
		document.body.style.overflow = 'hidden';
		if (instant) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => this.dialog?.classList.remove('no-transition'));
			});
		}
	}

	close() {
		if (this.dialog?.close) this.dialog.close();
		else this.dialog?.removeAttribute('open');
		document.body.style.overflow = '';
	}

	prepareImages() {
		if (this.imagesPrepared) return;
		this.imagesPrepared = true;
		this.dialogImages.forEach((image, index) => {
			image.loading = 'eager';
			image.decoding = 'async';
			image.fetchPriority = index < 8 ? 'high' : 'low';
		});
	}

	pickCard(card) {
		const kind = card.dataset.kind;
		this.cards
			.filter((c) => c.dataset.kind === kind)
			.forEach((c) => c.classList.remove('is-selected'));
		card.classList.add('is-selected');
		this.pendingCard = card;
		this.updateFooter(card);

		const url = card.dataset.productUrl;
		if (!url || url === '#') return;
		const target = new URL(url, window.location.origin);
		if (target.pathname === window.location.pathname) return;
		const withParams = FitStylePicker.preserveMaterialPickerParams(target.href);
		this.navigateTo(withParams, card);
	}

	updateFooter(card) {
		if (this.footerSwatch) {
			const url = card.dataset.image;
			this.footerSwatch.innerHTML = url ? `<img src="${url}" alt="">` : '';
		}
		if (this.footerName) this.footerName.textContent = card.dataset.name || '';
		if (this.footerPrice) this.footerPrice.textContent = card.dataset.price || '';
	}

	commit() {
		if (!this.pendingCard) {
			this.close();
			return;
		}
		const url = this.pendingCard.dataset.productUrl;
		if (!url || url === '#') {
			this.close();
			return;
		}
		const target = new URL(url, window.location.origin);
		if (target.pathname === window.location.pathname) {
			this.close();
			return;
		}
		const withParams = FitStylePicker.preserveMaterialPickerParams(target.href);
		this.navigateTo(withParams, this.pendingCard);
	}

	static preserveMaterialPickerParams(href) {
		const url = new URL(href, window.location.origin);
		const src = new URLSearchParams(window.location.search);
		['mp_color', 'mp_fabric'].forEach((key) => {
			const val = src.get(key);
			if (val) url.searchParams.set(key, val);
		});
		return url.toString();
	}

	async navigateTo(href, card, options = {}) {
		if (FitStylePicker._navigating) return;
		FitStylePicker._navigating = true;
		card?.classList.add('is-loading');
		this.classList.add('is-navigating');
		FitStylePicker.showLoading();
		const pushHistory = options.pushHistory !== false;

		const oldWrapper = document.getElementById(`shopify-section-${this.sectionId}`);
		const wantedLocation = this.location;
		const wasOpen = !!this.dialog?.open;

		try {
			const requestUrl = new URL(href, window.location.origin);
			if (this.sectionId) requestUrl.searchParams.set('section_id', this.sectionId);
			const res = await fetch(requestUrl, { headers: { Accept: 'text/html' } });
			if (!res.ok) throw new Error(`page ${res.status}`);
			const html = await res.text();

			const doc = new DOMParser().parseFromString(html, 'text/html');

			const newWrapper =
				(this.sectionId && doc.getElementById(`shopify-section-${this.sectionId}`)) ||
				doc.querySelector('fit-style-picker')?.closest('.shopify-section');

			if (!newWrapper) throw new Error('new section not found in target page');

			const targetWrapper = oldWrapper || document.querySelector('fit-style-picker')?.closest('.shopify-section');
			if (!targetWrapper) throw new Error('current section wrapper missing');

			if (doc.title) document.title = doc.title;
			if (pushHistory) history.pushState({ fitStylePicker: true }, '', href);

			targetWrapper.innerHTML = newWrapper.innerHTML;
			if (targetWrapper.id !== newWrapper.id) targetWrapper.id = newWrapper.id;
			FitStylePicker.runScripts(targetWrapper);

			requestAnimationFrame(() => {
				const newPicker =
					targetWrapper.querySelector(
						`fit-style-picker[data-location="${wantedLocation}"]`,
					) || targetWrapper.querySelector('fit-style-picker');
				if (newPicker && wasOpen) newPicker.open({ instant: true });
				document.dispatchEvent(
					new CustomEvent('fit-style-picker:navigated', { detail: { url: href } }),
				);
			});
		} catch (err) {
			console.error('[fit-style-picker] navigation failed, falling back to full reload', err);
			window.location.href = href;
		} finally {
			FitStylePicker._navigating = false;
			FitStylePicker.hideLoading();
		}
	}

	static showLoading() {
		if (document.getElementById('fit-style-picker-loading')) return;
		const overlay = document.createElement('div');
		overlay.id = 'fit-style-picker-loading';
		overlay.className = 'fit-style-picker-loading';
		overlay.innerHTML = '<div class="fit-style-picker-loading__spinner" role="status" aria-label="Loading"></div>';
		document.body.appendChild(overlay);
	}

	static hideLoading() {
		document.getElementById('fit-style-picker-loading')?.remove();
	}

	static runScripts(root) {
		root.querySelectorAll('script').forEach((oldScript) => {
			if (oldScript.src) return;
			const newScript = document.createElement('script');
			for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
			newScript.textContent = oldScript.textContent;
			oldScript.parentNode.replaceChild(newScript, oldScript);
		});
	}
}

if (!customElements.get('fit-style-picker')) {
	customElements.define('fit-style-picker', FitStylePicker);
}

if (!window.__fitStylePickerPopstateBound) {
	window.__fitStylePickerPopstateBound = true;
	window.addEventListener('popstate', () => {
		const picker = document.querySelector('fit-style-picker');
		if (!picker) return;
		picker.navigateTo(window.location.href, null, { pushHistory: false }).catch(() => window.location.reload());
	});
}
